import React, { useCallback, useMemo, useState } from "react";
import {
  Badge,
  ExpandableSection,
  Header,
  SpaceBetween,
  Tabs,
} from "@amzn/awsui-components-react";
import CbeResults from "./CbeResults";
import CbeHealthStatusDisplay from "./CbeHealthStatusDisplay";
import { CanonicalBusinessEvent } from "../../types";
import VersionSelector from "../common/VersionSelector";
import { groupCbesByProductIds } from "../../helpers/bundleUtils";
import { BADGE_COLORS } from "../../helpers/errorUtils";

interface Props {
  cbeData: CanonicalBusinessEvent[];
  onVersionChange: (index: number) => void;
  allProductIds: string[];
  showCbeProductBreakdown: boolean;
  onProductsToggle?: (toggleFunction: () => void) => void;
  onVersionViewToggle?: (
    toggleFunction: () => void,
    isTopLevel: boolean,
    shouldShow: boolean
  ) => void;
  initialIndex?: number;
}

function CbeVersionSelector({
  cbeData,
  onVersionChange,
  allProductIds,
  showCbeProductBreakdown,
  onProductsToggle,
  onVersionViewToggle,
  initialIndex,
}: Props) {
  const [allProductsExpanded, setAllProductsExpanded] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(0);
  const [topLevelVersionView, setTopLevelVersionView] = useState(false);

  const productGroups = useMemo(() => {
    if (!showCbeProductBreakdown) {
      return [];
    }

    const groups = groupCbesByProductIds(cbeData, allProductIds);

    if (groups.size === 0) {
      return [];
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([productId, cbes]) => ({
        productId,
        cbes,
      }));
  }, [cbeData, allProductIds, showCbeProductBreakdown]);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    () =>
      productGroups.reduce((acc, group) => {
        acc[group.productId] = true;
        return acc;
      }, {} as Record<string, boolean>)
  );

  const allVersions = useMemo(() => {
    const versions = new Set<number>();
    cbeData.forEach((cbe) => versions.add(cbe.lockVersion));
    return Array.from(versions).sort((a, b) => b - a);
  }, [cbeData]);

  const toggleAllProducts = useCallback(() => {
    const newState = !allProductsExpanded;
    setAllProductsExpanded(newState);

    const newExpandedGroups = productGroups.reduce((acc, group) => {
      acc[group.productId] = newState;
      return acc;
    }, {} as Record<string, boolean>);
    setExpandedGroups(newExpandedGroups);
  }, [allProductsExpanded, productGroups]);

  const toggleVersionView = useCallback(() => {
    setTopLevelVersionView((prev) => !prev);
  }, []);

  React.useEffect(() => {
    if (
      onProductsToggle &&
      showCbeProductBreakdown &&
      productGroups.length > 1
    ) {
      onProductsToggle(toggleAllProducts);
    }
  }, [
    showCbeProductBreakdown,
    productGroups.length,
    toggleAllProducts,
    onProductsToggle,
  ]);

  React.useEffect(() => {
    if (onVersionViewToggle) {
      const shouldShow = showCbeProductBreakdown && productGroups.length > 1;
      onVersionViewToggle(toggleVersionView, topLevelVersionView, shouldShow);
    }
  }, [
    showCbeProductBreakdown,
    productGroups.length,
    toggleVersionView,
    topLevelVersionView,
    onVersionViewToggle,
  ]);

  const handleBundleVersionChange = useCallback(
    (groupIndex: number) => (relativeIndex: number) => {
      let absoluteIndex = 0;
      for (let i = 0; i < groupIndex; i += 1) {
        absoluteIndex += productGroups[i].cbes.length;
      }
      onVersionChange(absoluteIndex + relativeIndex);
    },
    [productGroups, onVersionChange]
  );

  const renderTabLabel = useCallback(
    (version: number, cbesForVersion: CanonicalBusinessEvent[]) => (
      <CbeHealthStatusDisplay
        name="Version"
        events={cbesForVersion}
        value={version.toString()}
      />
    ),
    []
  );

  const renderTabContent = useCallback(
    (cbesForVersion: CanonicalBusinessEvent[]) => (
      <CbeResults cbeData={cbesForVersion} />
    ),
    []
  );

  const renderProductHeader = useCallback(
    (productId: string, cbes: CanonicalBusinessEvent[], index: number) => (
      <Header
        variant="h3"
        description={
          <small>
            {cbes
              .map((cbe) => cbe.usecaseName)
              .filter((name, idx, arr) => name && arr.indexOf(name) === idx)
              .join(", ") || ""}
          </small>
        }
      >
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Badge color={BADGE_COLORS[index % BADGE_COLORS.length]}>
            {index + 1}
          </Badge>
          Product: {productId}
        </SpaceBetween>
      </Header>
    ),
    []
  );

  if (productGroups.length === 0) {
    return (
      <VersionSelector
        data={cbeData}
        onVersionChange={onVersionChange}
        getVersion={(cbe) => cbe.lockVersion}
        renderTabLabel={renderTabLabel}
        renderTabContent={renderTabContent}
        initialIndex={initialIndex}
      />
    );
  }

  if (topLevelVersionView) {
    const currentVersion = allVersions[selectedVersion];
    const cbesForVersion = cbeData.filter(
      (cbe) => cbe.lockVersion === currentVersion
    );

    return (
      <>
        <Tabs
          activeTabId={selectedVersion.toString()}
          onChange={({ detail }) => {
            const newIndex = parseInt(detail.activeTabId, 10);
            const newVersion = allVersions[newIndex];
            const newCbesForVersion = cbeData.filter(
              (cbe) => cbe.lockVersion === newVersion
            );
            setSelectedVersion(newIndex);
            onVersionChange(cbeData.indexOf(newCbesForVersion[0]));
          }}
          tabs={allVersions.map((version, index) => ({
            id: index.toString(),
            label: renderTabLabel(
              version,
              cbeData.filter((cbe) => cbe.lockVersion === version)
            ),
            content: null,
          }))}
        />
        <SpaceBetween size="l">
          {productGroups.map((group, index) => {
            const productCbesForVersion = group.cbes.filter(
              (cbe) => cbe.lockVersion === currentVersion
            );
            if (productCbesForVersion.length === 0) return null;

            return (
              <ExpandableSection
                key={group.productId}
                variant="container"
                expanded={
                  expandedGroups[group.productId] ?? allProductsExpanded
                }
                onChange={(event) =>
                  setExpandedGroups((prev) => ({
                    ...prev,
                    [group.productId]: event.detail.expanded,
                  }))
                }
                header={renderProductHeader(
                  group.productId,
                  productCbesForVersion,
                  index
                )}
              >
                <CbeResults cbeData={productCbesForVersion} />
              </ExpandableSection>
            );
          })}
        </SpaceBetween>
      </>
    );
  }

  return (
    <SpaceBetween size="l">
      {productGroups.map((group, index) => (
        <ExpandableSection
          key={group.productId}
          variant="container"
          expanded={expandedGroups[group.productId] ?? allProductsExpanded}
          onChange={(event) =>
            setExpandedGroups((prev) => ({
              ...prev,
              [group.productId]: event.detail.expanded,
            }))
          }
          header={renderProductHeader(group.productId, group.cbes, index)}
        >
          <VersionSelector
            data={group.cbes}
            onVersionChange={handleBundleVersionChange(index)}
            getVersion={(cbe) => cbe.lockVersion}
            renderTabLabel={renderTabLabel}
            renderTabContent={renderTabContent}
          />
        </ExpandableSection>
      ))}
    </SpaceBetween>
  );
}

export default React.memo(CbeVersionSelector);
